import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, XCircle, FileText, Briefcase, MapPin, Calendar, IndianRupee,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_sriman-enterprise/artifacts/ng31c679_image.png";

const fmtINR = (n) => new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR", maximumFractionDigits: 0,
}).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

export default function OfferRespond() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // 'accepted' | 'declined'
  const [declineMode, setDeclineMode] = useState(false);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/offers/${token}`);
      setOffer(data);
      if (data.status === "accepted" || data.status === "declined") setDone(data.status);
    } catch {
      setNotFound(true);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const respond = async (action) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/offers/${token}/respond`, {
        action,
        reason: action === "decline" ? reason : undefined,
      });
      setDone(data.status);
      toast.success(action === "accept" ? "Offer accepted" : "Offer declined");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <main data-testid="offer-respond-page" className="min-h-screen bg-[#040812] flex items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
      <div className="relative w-full max-w-xl">
        <Link to="/" className="flex items-center gap-3 mb-8 justify-center">
          <span className="h-10 w-10">
            <img src={LOGO_URL} alt="Sriman Solutions" className="h-full w-full object-contain" />
          </span>
          <div className="flex flex-col leading-none">
            <span className="font-display text-white tracking-tight">Sriman Solutions</span>
            <span className="font-mono text-[9px] text-red-500 tracking-[0.28em] uppercase mt-1">Offer of Employment</span>
          </div>
        </Link>

        <div className="bg-[#0B132B]/80 border border-white/10">
          {loading && (
            <div className="p-16 text-center">
              <Loader2 size={24} className="inline animate-spin text-slate-500" />
            </div>
          )}

          {notFound && !loading && (
            <div className="p-10 text-center" data-testid="offer-not-found">
              <XCircle size={28} className="mx-auto text-red-500" />
              <h1 className="mt-4 font-display text-white text-2xl">This offer link is invalid.</h1>
              <p className="mt-2 text-slate-400 text-sm">
                It may have expired or been typed incorrectly. Please contact{" "}
                <a href="mailto:hr@srimansolutions.com" className="text-red-400">hr@srimansolutions.com</a>.
              </p>
            </div>
          )}

          {offer && !loading && (
            <div data-testid="offer-summary">
              <div className="p-8 border-b border-white/10">
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-red-500">
                  {offer.offer_no}
                </div>
                <h1 className="mt-3 font-display text-white text-3xl tracking-tight font-light">
                  {offer.candidate_name}, here's your offer.
                </h1>

                <div className="mt-6 space-y-3 text-sm">
                  <div className="flex items-center gap-3 text-slate-300">
                    <Briefcase size={14} className="text-red-500 shrink-0" />
                    <span><span className="text-white font-medium">{offer.role_title}</span> · {offer.department}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <MapPin size={14} className="text-red-500 shrink-0" />
                    <span>{offer.location || "Hyderabad, IN"}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <Calendar size={14} className="text-red-500 shrink-0" />
                    <span>Joining {fmtDate(offer.joining_date)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <IndianRupee size={14} className="text-red-500 shrink-0" />
                    <span><span className="text-white font-medium">{fmtINR(offer.ctc_inr)}</span> annual CTC</span>
                  </div>
                </div>

                <a
                  href={`${API}/offers/${token}/letter.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="offer-view-letter"
                  className="mt-6 inline-flex items-center gap-2 border border-white/15 hover:border-red-500 text-white text-xs px-4 py-2.5 rounded-sm transition-colors"
                >
                  <FileText size={14} /> View full offer letter (PDF)
                </a>
              </div>

              {done ? (
                <div className="p-8 text-center" data-testid={`offer-${done}`}>
                  {done === "accepted" ? (
                    <>
                      <CheckCircle2 size={30} className="mx-auto text-green-400" />
                      <h2 className="mt-4 font-display text-white text-2xl">Welcome aboard.</h2>
                      <p className="mt-2 text-slate-400 text-sm">
                        We've set up your HRMS account and Day-1 onboarding checklist.
                        Your login details will reach you by email shortly.
                      </p>
                    </>
                  ) : (
                    <>
                      <XCircle size={30} className="mx-auto text-slate-400" />
                      <h2 className="mt-4 font-display text-white text-2xl">Offer declined.</h2>
                      <p className="mt-2 text-slate-400 text-sm">
                        Thanks for letting us know. We wish you the very best.
                      </p>
                    </>
                  )}
                </div>
              ) : declineMode ? (
                <div className="p-8" data-testid="offer-decline-form">
                  <label className="overline mb-2 block">Reason (optional)</label>
                  <textarea
                    rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Compensation, timing, role fit…"
                    data-testid="offer-decline-reason"
                    className="w-full bg-[#040812]/60 border border-white/10 focus:border-red-500 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none"
                  />
                  <div className="mt-4 flex gap-3">
                    <button onClick={() => setDeclineMode(false)}
                            className="flex-1 border border-white/15 text-slate-300 text-sm px-4 py-2.5 rounded-sm">
                      Back
                    </button>
                    <button onClick={() => respond("decline")} disabled={busy}
                            data-testid="offer-decline-confirm"
                            className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-sm px-4 py-2.5 rounded-sm">
                      {busy ? <Loader2 size={14} className="inline animate-spin" /> : "Confirm decline"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-8 flex gap-3">
                  <button onClick={() => respond("accept")} disabled={busy}
                          data-testid="offer-accept-button"
                          className="flex-1 inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-sm font-medium px-4 py-3.5 rounded-sm">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    Accept offer
                  </button>
                  <button onClick={() => setDeclineMode(true)} disabled={busy}
                          data-testid="offer-decline-button"
                          className="flex-1 border border-white/15 hover:border-red-500 text-white text-sm px-4 py-3.5 rounded-sm transition-colors">
                    Decline
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-slate-600 text-xs font-mono tracking-widest uppercase">
          Secure link · Sriman Solutions
        </p>
      </div>
    </main>
  );
}
